import { useState, useEffect } from 'react'
import { Link, usePage } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Menu, X, ArrowRight } from 'lucide-react'
import type { PageProps, User } from '@/types'

interface LandingNavbarProps {
  user?: User | null
}

export function LandingNavbar({ user: propUser }: LandingNavbarProps) {
  const pageProps = usePage<PageProps>().props
  const user = propUser !== undefined ? propUser : pageProps?.auth?.user
  const isStaff = user?.role === 'admin' || user?.role === 'reviewer' || (user as { role?: string })?.role === 'super_admin'
  const dashboardUrl = isStaff ? '/admin' : '/student'

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const isHomePage = typeof window !== 'undefined' ? (window.location.pathname === '/' || window.location.pathname === '') : true

  const [activeSection, setActiveSection] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      const hash = window.location.hash
      if (hash === '#exams') return 'exams'
      if (hash === '#pricing') return 'pricing'
      if (window.location.pathname === '/' || window.location.pathname === '') return 'home'
    }
    return ''
  })

  useEffect(() => {
    if (!isHomePage || typeof window === 'undefined') {
      setActiveSection('')
      return
    }

    const updateActiveSection = () => {
      const scrollY = window.scrollY
      const pricingEl = document.getElementById('pricing')
      const examsEl = document.getElementById('exams')

      if (pricingEl) {
        const pricingRect = pricingEl.getBoundingClientRect()
        // If pricing section has reached top half of viewport
        if (pricingRect.top <= 200 && pricingRect.bottom >= 100) {
          setActiveSection('pricing')
          return
        }
      }

      if (examsEl) {
        const examsRect = examsEl.getBoundingClientRect()
        // If exams section is within top half of viewport
        if (examsRect.top <= 250 && examsRect.bottom >= 100) {
          setActiveSection('exams')
          return
        }
      }

      // If near top or above exams section
      if (scrollY < 250) {
        setActiveSection('home')
      }
    }

    const handleHashChange = () => {
      const hash = window.location.hash
      if (hash === '#exams') setActiveSection('exams')
      else if (hash === '#pricing') setActiveSection('pricing')
      else if (window.scrollY < 250) setActiveSection('home')
    }

    // Check on initial render / mount
    if (window.location.hash === '#exams') {
      setActiveSection('exams')
    } else if (window.location.hash === '#pricing') {
      setActiveSection('pricing')
    } else {
      updateActiveSection()
    }

    window.addEventListener('scroll', updateActiveSection, { passive: true })
    window.addEventListener('hashchange', handleHashChange)

    return () => {
      window.removeEventListener('scroll', updateActiveSection)
      window.removeEventListener('hashchange', handleHashChange)
    }
  }, [isHomePage])

  const isExamsPage = typeof window !== 'undefined' ? window.location.pathname.startsWith('/exams') : false
  const isMockTestsPage = typeof window !== 'undefined' ? window.location.pathname.startsWith('/mock-tests') : false
  const isBlogPage = typeof window !== 'undefined' ? window.location.pathname.startsWith('/blog') : false

  const navLinks = [
    { label: 'Home', href: isHomePage ? '#' : '/', id: 'home', active: isHomePage && activeSection === 'home', isPageLink: !isHomePage },
    { label: 'Exams', href: '/exams', id: 'exams', active: isExamsPage, isPageLink: true },
    { label: 'Mock Tests', href: '/mock-tests', id: 'mock-tests', active: isMockTestsPage, isPageLink: true },
    { label: 'Blog', href: '/blog', id: 'blog', active: isBlogPage, isPageLink: true },
    { label: 'Pricing', href: isHomePage ? '#pricing' : '/#pricing', id: 'pricing', active: isHomePage && activeSection === 'pricing', isPageLink: false },
  ]

  const scrollToSection = (e: React.MouseEvent<HTMLAnchorElement>, link: typeof navLinks[0]) => {
    if (link.isPageLink) {
      return
    }
    if (link.href === '#') {
      e.preventDefault()
      setActiveSection('home')
      if (window.location.hash) {
        window.history.pushState(null, '', window.location.pathname)
      }
      window.scrollTo({ top: 0, behavior: 'smooth' })
      setMobileMenuOpen(false)
      return
    }
    if (link.href.startsWith('#') && link.href.length > 1) {
      const element = document.querySelector(link.href)
      if (element) {
        e.preventDefault()
        setActiveSection(link.id)
        window.history.pushState(null, '', link.href)
        element.scrollIntoView({ behavior: 'smooth' })
        setMobileMenuOpen(false)
      }
    }
  }

  const handleLogoClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (isHomePage) {
      e.preventDefault()
      setActiveSection('home')
      if (window.location.hash) {
        window.history.pushState(null, '', window.location.pathname)
      }
      window.scrollTo({ top: 0, behavior: 'smooth' })
      setMobileMenuOpen(false)
    }
  }

  return (
    <header className="w-full bg-white border-b border-slate-100 sticky top-0 z-50 shadow-xs">
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          
          {/* Logo */}
          <Link
            href="/"
            onClick={handleLogoClick}
            className="flex items-center gap-3 group focus:outline-none cursor-pointer"
            title="PrepKiya"
          >
            <img
              src="/logo.svg"
              alt="PrepKiya Logo"
              className="w-10 h-10 rounded-xl object-contain shadow-xs transition-transform group-hover:scale-105"
            />
            <span className="!font-sans font-black text-2xl tracking-tight text-[#131b2e]">
              Prep<span className="text-brand-gold">Kiya</span>
            </span>
          </Link>

          {/* Center Navigation Links */}
          <nav className="hidden md:flex items-center gap-8 lg:gap-10">
            {navLinks.map((link) => (
              link.isPageLink ? (
                <Link
                  key={link.label}
                  href={link.href}
                  className={`!font-sans text-sm font-medium transition-colors ${
                    link.active
                      ? 'text-brand-gold font-bold'
                      : 'text-slate-600 hover:text-[#131b2e]'
                  }`}
                >
                  {link.label}
                </Link>
              ) : (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={(e) => scrollToSection(e, link)}
                  className={`!font-sans text-sm font-medium transition-colors ${
                    link.active
                      ? 'text-brand-gold font-bold'
                      : 'text-slate-600 hover:text-[#131b2e]'
                  }`}
                >
                  {link.label}
                </a>
              )
            ))}
          </nav>

          {/* Right Action Buttons */}
          <div className="hidden sm:flex items-center gap-5">
            {user ? (
              <Button asChild className="!font-sans bg-[#131b2e] hover:bg-[#1e293b] text-white font-bold rounded-lg px-6 h-11 shadow-sm transition-all cursor-pointer">
                <Link href={dashboardUrl}>
                  Dashboard
                  <ArrowRight className="w-4 h-4 ml-1.5" />
                </Link>
              </Button>
            ) : (
              <>
                <a
                  href="/users/sign_in"
                  className="!font-sans text-sm font-semibold text-slate-700 hover:text-[#131b2e] px-2 py-1 transition-colors"
                >
                  Login
                </a>
                <Button asChild className="!font-sans bg-[#131b2e] hover:bg-[#1e293b] text-white font-bold rounded-lg px-6 h-11 shadow-sm transition-all">
                  <a href="/users/sign_up">Get Started</a>
                </Button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="flex items-center gap-2 sm:hidden">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle Menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6 text-[#131b2e]" /> : <Menu className="w-6 h-6 text-[#131b2e]" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="sm:hidden bg-white border-b border-slate-200 px-4 py-4 space-y-3 shadow-lg">
          <nav className="flex flex-col space-y-2">
            {navLinks.map((link) => (
              link.isPageLink ? (
                <Link
                  key={link.label}
                  href={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`!font-sans px-3 py-2 text-sm font-medium rounded-lg ${
                    link.active ? 'text-brand-gold font-bold bg-brand-gold/10' : 'text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  {link.label}
                </Link>
              ) : (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={(e) => scrollToSection(e, link)}
                  className={`!font-sans px-3 py-2 text-sm font-medium rounded-lg ${
                    link.active ? 'text-brand-gold font-bold bg-brand-gold/10' : 'text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  {link.label}
                </a>
              )
            ))}
          </nav>
          <div className="pt-3 border-t border-slate-100 flex flex-col gap-2">
            {user ? (
              <Button asChild className="!font-sans w-full bg-[#131b2e] text-white font-bold cursor-pointer">
                <Link href={dashboardUrl} onClick={() => setMobileMenuOpen(false)}>Dashboard</Link>
              </Button>
            ) : (
              <>
                <Button asChild variant="outline" className="!font-sans w-full bg-transparent hover:bg-slate-50 border-slate-300 text-slate-700">
                  <a href="/users/sign_in">Login</a>
                </Button>
                <Button asChild className="!font-sans w-full bg-[#131b2e] text-white">
                  <a href="/users/sign_up">Get Started</a>
                </Button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  )
}
